import { createContext, useContext, useState } from "react";

/**
 * @typedef {Object<string, string>} FormError
 * @property {string} name The name of the field the error is for.
 * @property {string} message The error Message.
 */

/** @typedef {HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement} FormField */

/**
 * @callback Validator
 * @param {any} value The value of the field being validated.
 * @param {HTMLElement} form The actual form.
 * @return {string | undefined | false} An error message if there is a problem.
 */

/**
 * @typedef {Object<string, any>} Context
 * @property {string} id The id of the current form or fieldset.
 * @property {string} name The full, scoped name of the current fieldset.
 * @property {string} label The human-readable name of the current fieldset.
 * @property {boolean} required True if all child fields are required.
 * @property {string | Function} LegendTag The JSX tag to be used by any child
 *   legend elements.
 * @property {Object} options Additional data to store in the context.
 * @property {FormBrain} form The current form brain.
 */

const FormContext = createContext({
    id: "",
    name: "",
    label: "",
    required: false,
    LegendTag: "legend",
    options: {},
    form: null,
});

/**
 * Hook to acquire the current, closest-scoped form context.
 *
 * @return {Context} An object with details about the context a field is
 *   being rendered in.
 */
const useFormContext = () => {
    const context = /** @type {Context} */ (useContext(FormContext));

    return context;
};

/**
 * Converts all validation configuration to be arrays of functions.
 *
 * This simplifies all the other validation functions from having to handle both
 * functions, and arrays of functions.
 *
 * @param {Object<string, Validator | Validator[]>} validation Validation methods.
 *
 * @return {Object<string, Validator[]>} The validation object, but all fields
 *   have arrays of validation functions now.
 */
function normalizeValidation(validation) {
    const fields = Object.keys(validation);

    return fields.reduce((val, fieldName) => {
        const functions = validation[fieldName];

        if (!Array.isArray(functions)) {
            val[fieldName] = [functions];
        } else {
            val[fieldName] = functions;
        }

        return val;
    }, {});
}

/**
 * The brains of any SmartForm.
 */
class FormBrain {

    /**
     * Brain constructor.
     *
     * @param {[Object<string, any>, Function]} valueState The value state, as provided by `useState`.
     * @param {[FormError[], Function]} errorState The error state, as provided by `useState`.
     * @param {Object<string, Validator[]>} validation Validation methods.
     * @param {[boolean, Function]} revisingState The revising state, as provided by `useState`.
     */
    constructor([values, setValues], [errors, setErrors], validation, [revising, setRevising]) {
        this.values = values;
        this._setValues = setValues;
        this.errors = errors;
        this._setErrors = setErrors;
        this.validation = validation;
        this.revising = revising;
        this._setRevising = setRevising;
    }

    /**
     * Creates a react context provider for the form or a fieldset.
     *
     * @param {string} id The form or fieldset id.
     * @param {string} [name = ""] The fieldset name.
     * @param {string} [label = ""] The human-readable name for the fieldset.
     * @param {Object<string, any>} [options] Additional options for nesting.
     * @param {boolean} [options.required = false] True if every field in this
     *   context is required.
     * @param {string | Function} [options.LegendTag = "legend"] The tag to use
     *   when rendering a `<Legend />` component in this context.
     * @param {string} [options.barename = ""] The unscoped name of the fieldset.
     *
     * @return {Object} A `Provider` and `value` to create a React context.
     */
    createScope(
        id,
        name = "",
        label = "",
        {
            required = false,
            LegendTag = "legend",
            bareName = "",
            ...options
        } = {}
    ) {
        /** @var {FormContext} context */
        const context = {
            id,
            name,
            label,
            required,
            LegendTag,
            bareName,
            options,
            form: this,
            getScopedName: (fieldName) => {
                if (fieldName === bareName) {
                    return name;
                }
                return name ? `${name}[${fieldName}]` : fieldName;
            }
        };

        return {
            Provider: FormContext.Provider,
            value: context,
        };
    }

    /**
     * Puts the form into "Revising" mode, which enables live validation.
     *
     * @see https://uxmovement.com/forms/why-users-make-more-errors-with-instant-inline-validation/
     *
     * @param {bool} [enable = true] Whether to enabled or disable the mode.
     */
    enableRevisingMode(enable = true) {
        if (this.isRevising() !== enable) {
            this._setRevising(enable);
        }
    }

    /**
     * Checks if the form is in revising mode.
     *
     * @return {bool} True if revising mode is on.
     */
    isRevising() {
        return this.revising;
    }

    /**
     * Retrieve the current value for a field.
     *
     * @param {string} fieldName The name of the field to pull a value for.
     * @param {any} [defaultValue] The value to return if there is no value for
     *   this field on the form. This is different from the field's default
     *   value, which this function already accounts for, and is intended to
     *   make it easier to work with values without having to check types.
     *
     * @return {any} The current value of the field, or `defaultValue`.
     */
    getValue(fieldName, defaultValue = undefined) {
        return (this.values.hasOwnProperty(fieldName)
            ? this.values[fieldName]
            : defaultValue
        );
    }

    /**
     * Get the entire set of current field values.
     *
     * @return {Object<string, any>}
     */
    getValues() {
        return this.values;
    }

    /**
     * Converts the flat values object to a structured object.
     *
     * For example, if values is:
     *
     * ```
     * {
     *   "name": "example",
     *   "option[0]": true,
     *   "option[1]": false,
     * }
     * ```
     *
     * Then this will return
     *
     * ```
     * {
     *   "name": "example",
     *   "option": [true, false],
     * }
     * ```
     *
     * @return {Object}
     */
    getValuesAsObject() {
        const valueObject = {};
        const isInt = (x) => x === `${parseInt(x)}`;

        const deepSet = (target, current, remaining, value) => {
            const next = remaining.pop();

            if (!next) {
                target[current] = value;
                return;
            }

            const preferArray = isInt(next);

            if (typeof target[current] !== "object") {
                target[current] = preferArray ? [] : {};
            } else if (!preferArray && Array.isArray(target[current])) {
                target[current] = {...target[current]};
            }

            deepSet(target[current], next, remaining, value);
        };

        Object.entries(this.values).forEach(([name, value]) => {
            const keys = name.split(/[[\]]+/).reverse();

            deepSet(valueObject, keys.pop(), keys, value);
        });

        return valueObject;
    }

    /**
     * Store the current value of a field in the state.
     *
     * @param {string} fieldName The full, scoped name of the field.
     * @param {any} value The value to store for the field.
     */
    setValue(fieldName, value) {
        this._setValues({
            ...this.values,
            [fieldName]: value,
        });
    }

    /**
     * Updates the entire values object.
     *
     * @param {Object} values
     */
    setValues(values) {
        this._setValues(values || {});
    }

    /**
     * Get all the errors for a given field.
     *
     * @param {string} fieldName The full, scoped name of the field to check.
     *
     * @return {FormError[]}
     */
    findErrors(fieldName) {
        return this.errors.filter(({name}) => name === fieldName);
    }

    /**
     * Stores a new set of errors for a field.
     *
     * This will remove existing errors for the given field.
     *
     * @param {string} fieldName The full, scoped name of the field.
     * @param {FormError[]} errors The new set of errors to store.
     */
    setErrors(fieldName, errors) {
        const oldErrors = this.errors.filter(({name}) => name !== fieldName);

        this.enableRevisingMode();
        this._setErrors(oldErrors.concat(errors));
    }

    /**
     * Overrides the entire errors array with a new version.
     *
     * @param {FormError[]} errors The new errors.
     */
    setAllErrors(errors) {
        this.enableRevisingMode();
        this._setErrors(errors || []);
    }

    /**
     * Register a new validation function for a field.
     *
     * An effort is made to not add duplicates
     *
     * @param {string} fieldName The full, scoped name of the field.
     * @param {Validator} validator The new validation function.
     */
    addValidator(fieldName, validator) {
        const fieldValidation = this.validation[fieldName] || [];

        if (!fieldValidation.includes(validator)) {
            fieldValidation.push(validator);
        }

        this.validation[fieldName] = fieldValidation;
    }

    /**
     * Run validation rules against the given field.
     *
     * @param {FormField} field The field element to check.
     *
     * @return {FormError[]} The errors, if any.
     */
    validateField(field) {
        /** @var {FormError[]} */
        const errors = [];

        if (!this.validation[field.name]) {
            return errors;
        }

        this.validation[field.name].forEach((validator) => {
            const error = validator(field.value, field.form);

            if (error) {
                errors.push({
                    name: field.name,
                    message: error,
                });
            }
        });

        return errors;
    }

    /**
     * Validate the whole form.
     *
     * @param {HTMLFormElement} form The form to validate.
     *
     * @return {FormError[]} The found errors, if any.
     */
    validate(form) {
        /** @var {FormError[]} */
        const errors = Object.keys(this.validation).reduce((errs, fieldName) => {
            if (!form.elements[fieldName]) return errs;

            return errs.concat(this.validateField(form.elements[fieldName]));
        }, []);

        this._setErrors([...errors]);

        return errors;
    }
}

/**
 * Hook: Sets up a `FormBrain` and related state.
 *
 * @param {Object<string, any>} defaults The default values for the form, keyed
 *   by the full, scoped name of the field.
 * @param {FormError[]} errors The starting errors for the form.
 * @param {Object<string, Validator | Validator[]>} validation Validation methods
 *   for the form, keyed by the full, scoped name of the field.
 * @param {boolean} revising True if the form is in revising mode.
 *
 * @return FormBrain
 */
const useFormBrain = (defaults, errors, validation, revising) => {
    const valueState = useState({...defaults});
    const errorState = useState(errors);
    const normalizedValidation = normalizeValidation(validation);
    const revisingState = useState(revising);

    return new FormBrain(valueState, errorState, normalizedValidation, revisingState);
};

export default useFormBrain;
export { useFormContext };
